import { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';

import { PregnancyStatus } from '@dreryk/egabinet-visit-frontend-api';

import { CheckIcon, NotIcon, PatientStatus, PatientStatusOptions, QuestionmarkIcon } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

import { visitPatientPregnancyStatusTranslationKeys } from '../../translations';

import { PatientPregnancyCompletionMarkingModal } from './PatientPregnancyCompletionMarkingModal';
import { PatientPregnancyMarkingModal } from './PatientPregnancyMarkingModal';
import { PatientPregnancyModalOpener } from './PatientPregnancyModalOpener';

const usePatientPregnancyStatusOptions = (): PatientStatusOptions<Record<PregnancyStatus, any>> => {
   const theme = useTheme();
   return useMemo<Record<PregnancyStatus, any>>(() => {
      return {
         PREGNANT: {
            icon: <CheckIcon />,
            backgroundColor: theme.success,
         },
         NOT_PREGNANT: {
            icon: <NotIcon />,
            backgroundColor: theme.background,
         },
         NO_INFORMATION: {
            icon: <QuestionmarkIcon />,
            backgroundColor: theme.warning,
         },
      };
   }, [theme.background, theme.warning, theme.success]);
};

const StyledPatientStatus = styled.div`
   cursor: pointer;
`;
StyledPatientStatus.displayName = 'StyledPatientStatus';

type PatientPregnancyStatusComponentProps = {
   readonly pregnancyStatus: PregnancyStatus;
   readonly visitId: number;
};

export const PatientPregnancyStatus = (props: PatientPregnancyStatusComponentProps): JSX.Element => {
   const [pregnancyStatus, setPregnancyStatus] = useState(props.pregnancyStatus);
   const options = usePatientPregnancyStatusOptions();
   const settingPatientPregnancyAvailable = protectFeature('settingPatientPregnancy');

   useEffect(() => {
      if (props.pregnancyStatus !== pregnancyStatus) {
         setPregnancyStatus(props.pregnancyStatus);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.pregnancyStatus]);

   const handlePregnancyMark = useCallback(() => {
      setPregnancyStatus('PREGNANT');
   }, []);

   const handlePregnancyCompletionMark = useCallback(() => {
      setPregnancyStatus('NOT_PREGNANT');
   }, []);

   return (
      <PatientPregnancyModalOpener>
         {({ modalOpened, openModal, closeModal }) => (
            <>
               <StyledPatientStatus onClick={settingPatientPregnancyAvailable ? openModal : undefined}>
                  <PatientStatus
                     options={options}
                     type={pregnancyStatus}
                     label={visitPatientPregnancyStatusTranslationKeys.pregnancy}
                     testId={'pregnancyStatus'}
                  />
               </StyledPatientStatus>
               {modalOpened && settingPatientPregnancyAvailable && props.pregnancyStatus === 'NOT_PREGNANT' ? (
                  <PatientPregnancyMarkingModal
                     visitId={props.visitId}
                     onClose={closeModal}
                     onMark={handlePregnancyMark}
                  />
               ) : null}
               {modalOpened && settingPatientPregnancyAvailable && props.pregnancyStatus === 'PREGNANT' ? (
                  <PatientPregnancyCompletionMarkingModal
                     visitId={props.visitId}
                     onClose={closeModal}
                     onMark={handlePregnancyCompletionMark}
                  />
               ) : null}
            </>
         )}
      </PatientPregnancyModalOpener>
   );
};
